/* GW Product Recommendations — <gw-product-recommendations>
   Shopify fills the Liquid `recommendations` object only when a section is
   rendered through the Product Recommendations API, so the product page ships
   this host empty and the element fetches its own section from `data-url`
   (routes.product_recommendations_url + section_id + product_id + limit +
   intent) as it nears the viewport — immediately in the theme editor.

   Split contract (the gw-judgeme-reviews one): a response carrying cards
   ([data-gw-pr-loaded]) replaces the host's contents; no cards, or a failed
   request, removes the whole section wrapper on preview/live and shows the
   <template data-gw-pr-placeholder> the Liquid emits only in the editor.

   Written rather than reusing the vendor <product-recommendations>: that
   element cannot remove its section when empty, re-fetches on every
   shopify:section:load in the editor, and dispatches theme:section:load /
   recommendations:loaded, which re-initialise unrelated sliders.

   The injected <gw-scroll-carousel> upgrades on insertion, so its arrows,
   snap paging and progress thumb bind with no refresh() call. The response is
   parsed with DOMParser, whose document is inert: nothing in it loads until
   the markup lands in this one. */

const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

class GwProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.started || this.done || !this.dataset.url) return;

    if (designMode() || !('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    /* 400px of lookahead so the cards are usually in before the reader
       arrives; the empty host itself has no height to lay out. */
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  /* A host moved or re-rendered mid-request aborts it; the next
     connectedCallback starts again unless the answer already arrived. */
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.controller) this.controller.abort();
    if (!this.done) this.started = false;
  }

  async load() {
    this.started = true;
    this.controller = new AbortController();

    try {
      const response = await fetch(this.dataset.url, {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );
      const fresh = doc.querySelector('gw-product-recommendations');

      if (fresh && fresh.querySelector('[data-gw-pr-loaded]')) {
        this.done = true;
        this.innerHTML = fresh.innerHTML;
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
    }

    this.done = true;
    this.empty();
  }

  /* Nothing to show. The editor keeps a placeholder so the merchant can still
     find and select the section; everywhere else it leaves no trace. */
  empty() {
    const placeholder = this.querySelector('template[data-gw-pr-placeholder]');
    if (designMode() && placeholder) {
      this.replaceChildren(placeholder.content.cloneNode(true));
      return;
    }
    (this.closest('.shopify-section') || this).remove();
  }
}

if (!customElements.get('gw-product-recommendations')) {
  customElements.define('gw-product-recommendations', GwProductRecommendations);
}

if (window.wetheme && window.wetheme.webcomponentRegistry) {
  window.wetheme.webcomponentRegistry.register({
    key: 'gw-product-recommendations',
    element: 'gw-product-recommendations',
  });
}
